export function detectMediaCapabilities(scope = globalThis) {
    const values = scope;
    return Object.freeze({
        htmlMediaElement: typeof values.HTMLVideoElement === "function",
        imageBitmap: typeof values.createImageBitmap === "function",
        videoFrame: typeof values.VideoFrame === "function",
    });
}
export const MAX_MEDIA_BINDINGS = 256;
/** Host-owned loading/playback pipeline with one in-flight frame per Video. */
export class MediaPipeline {
    #options;
    #entries = new Map();
    #submittedFrames = 0;
    #droppedFrames = 0;
    #copiedFrames = 0;
    #releasedFrames = 0;
    #errors = 0;
    #inFlight = 0;
    #maximumInFlight = 0;
    #closed = false;
    constructor(options) {
        this.#options = options;
        if (typeof document !== "undefined") {
            document.addEventListener("visibilitychange", this.#handleVisibility);
        }
    }
    bind(binding, nodeId) {
        this.assertOpen();
        const current = this.#entries.get(nodeId);
        if (binding === undefined) {
            if (current !== undefined)
                this.disposeEntry(current);
            this.#entries.delete(nodeId);
            this.reportMetrics();
            return;
        }
        if (current !== undefined && samePlaybackSource(current.binding, binding)) {
            current.binding = binding;
            this.applyMutableOptions(current.video, binding);
            this.capture(current);
            this.reportMetrics();
            return;
        }
        if (current === undefined && this.#entries.size >= MAX_MEDIA_BINDINGS) {
            throw new RangeError(`media binding count exceeds ${String(MAX_MEDIA_BINDINGS)}`);
        }
        if (current !== undefined)
            this.disposeEntry(current);
        const video = this.createVideo();
        const entry = {
            binding,
            video,
            generation: 1,
            inFlight: false,
            pendingCapture: false,
            frameCallback: undefined,
            animationFrame: undefined,
            wasPlayingBeforeHidden: false,
            listeners: [],
        };
        this.#entries.set(nodeId, entry);
        this.configure(entry);
        this.reportMetrics();
    }
    play(nodeId) {
        const entry = this.requireEntry(nodeId);
        void entry.video.play().catch((cause) => this.emitError(entry, cause));
    }
    pause(nodeId) {
        this.requireEntry(nodeId).video.pause();
    }
    seek(nodeId, timeSeconds) {
        if (!Number.isFinite(timeSeconds) || timeSeconds < 0) {
            throw new RangeError("media seek time must be finite and non-negative");
        }
        const video = this.requireEntry(nodeId).video;
        video.currentTime = Number.isFinite(video.duration)
            ? Math.min(timeSeconds, video.duration)
            : timeSeconds;
    }
    metrics() {
        return Object.freeze({
            bindings: this.#entries.size,
            submittedFrames: this.#submittedFrames,
            droppedFrames: this.#droppedFrames,
            copiedFrames: this.#copiedFrames,
            releasedFrames: this.#releasedFrames,
            errors: this.#errors,
            inFlight: this.#inFlight,
            maximumInFlight: this.#maximumInFlight,
        });
    }
    close() {
        if (this.#closed)
            return;
        this.#closed = true;
        if (typeof document !== "undefined") {
            document.removeEventListener("visibilitychange", this.#handleVisibility);
        }
        for (const entry of this.#entries.values())
            this.disposeEntry(entry);
        this.#entries.clear();
        this.reportMetrics();
    }
    createVideo() {
        const video = this.#options.createVideo?.() ?? document.createElement("video");
        if (typeof video.play !== "function" || typeof video.pause !== "function") {
            throw new TypeError("media environment did not create an HTMLVideoElement-compatible object");
        }
        return video;
    }
    configure(entry) {
        const { binding, video } = entry;
        if (binding.crossOrigin !== undefined)
            video.crossOrigin = binding.crossOrigin;
        video.preload = binding.preload;
        video.playsInline = true;
        this.applyMutableOptions(video, binding);
        this.listen(entry, "loadedmetadata", () => {
            const width = video.videoWidth;
            const height = video.videoHeight;
            if (width > 0 && height > 0)
                this.#options.onMetadata?.(binding.nodeId, width, height);
            this.emitEvent(entry, "loadedmetadata");
            this.capture(entry);
        });
        this.listen(entry, "play", () => {
            this.emitEvent(entry, "play");
            this.scheduleFrames(entry);
        });
        this.listen(entry, "pause", () => this.emitEvent(entry, "pause"));
        this.listen(entry, "ended", () => this.emitEvent(entry, "ended"));
        this.listen(entry, "timeupdate", () => this.emitEvent(entry, "timeupdate"));
        this.listen(entry, "seeked", () => this.capture(entry));
        this.listen(entry, "error", () => this.emitMediaElementError(entry));
        video.src = binding.src;
        video.load();
        if (binding.autoPlay)
            void video.play().catch((cause) => this.emitError(entry, cause));
    }
    applyMutableOptions(video, binding) {
        video.loop = binding.loop;
        video.muted = binding.muted;
    }
    scheduleFrames(entry) {
        const video = entry.video;
        if (video.requestVideoFrameCallback !== undefined) {
            const tick = () => {
                if (!this.isCurrent(entry) || entry.video.paused || entry.video.ended)
                    return;
                this.capture(entry);
                entry.frameCallback = video.requestVideoFrameCallback?.(tick);
            };
            entry.frameCallback = video.requestVideoFrameCallback(tick);
            return;
        }
        const tick = () => {
            if (!this.isCurrent(entry) || entry.video.paused || entry.video.ended)
                return;
            this.capture(entry);
            entry.animationFrame = requestAnimationFrame(tick);
        };
        entry.animationFrame = requestAnimationFrame(tick);
    }
    capture(entry) {
        if (!this.isCurrent(entry) || entry.video.readyState < 2)
            return;
        if (!this.#options.transferableFrames) {
            this.#options.target.submit(entry.binding.resourceId, entry.video, "html-media");
            this.#submittedFrames += 1;
            this.reportMetrics();
            return;
        }
        if (entry.inFlight) {
            // Coalesce a producer burst into one pending capture. The current copy
            // is discarded when it completes, so the newest frame wins while the
            // transferable queue remains strictly bounded.
            entry.pendingCapture = true;
            this.#droppedFrames += 1;
            this.reportMetrics();
            return;
        }
        const generation = entry.generation;
        entry.inFlight = true;
        this.#inFlight += 1;
        this.#maximumInFlight = Math.max(this.#maximumInFlight, this.#inFlight);
        let frame;
        try {
            frame = this.#options.createVideoFrame?.(entry.video);
        }
        catch (cause) {
            this.emitError(entry, cause);
        }
        if (frame !== undefined) {
            this.finishCapture(entry, generation, frame, "video-frame");
            return;
        }
        const create = this.#options.createImageBitmap ?? globalThis.createImageBitmap;
        if (typeof create !== "function") {
            this.finishCapture(entry, generation, undefined, "image-bitmap");
            this.emitError(entry, new Error("transferable media frames are unavailable"));
            return;
        }
        void create(entry.video).then((bitmap) => this.finishCapture(entry, generation, bitmap, "image-bitmap"), (cause) => {
            this.finishCapture(entry, generation, undefined, "image-bitmap");
            this.emitError(entry, cause);
        });
    }
    finishCapture(entry, generation, source, path) {
        if (entry.inFlight) {
            entry.inFlight = false;
            this.#inFlight -= 1;
        }
        if (!this.isCurrent(entry) || entry.generation !== generation) {
            if (source !== undefined) {
                closeSource(source);
                this.#releasedFrames += 1;
            }
            this.reportMetrics();
            return;
        }
        if (entry.pendingCapture) {
            entry.pendingCapture = false;
            if (source !== undefined) {
                closeSource(source);
                this.#releasedFrames += 1;
            }
            this.capture(entry);
            this.reportMetrics();
            return;
        }
        if (source === undefined) {
            this.reportMetrics();
            return;
        }
        try {
            this.#options.target.submit(entry.binding.resourceId, source, path);
        }
        catch (cause) {
            this.emitError(entry, cause);
            return;
        }
        this.#submittedFrames += 1;
        if (path === "image-bitmap")
            this.#copiedFrames += 1;
        this.reportMetrics();
    }
    emitEvent(entry, type) {
        this.#options.onEvent?.(entry.binding.nodeId, Object.freeze({
            type,
            currentTime: finiteOrZero(entry.video.currentTime),
            duration: finiteOrZero(entry.video.duration),
        }));
    }
    emitMediaElementError(entry) {
        const code = entry.video.error?.code;
        const mapped = code === 1
            ? "aborted"
            : code === 2
                ? "network"
                : code === 3
                    ? "decode"
                    : code === 4
                        ? "not-supported"
                        : "unknown";
        this.emitError(entry, new Error(entry.video.error?.message || `media error ${String(code ?? 0)}`), mapped);
    }
    emitError(entry, cause, code = typeof DOMException === "function" &&
        cause instanceof DOMException &&
        cause.name === "SecurityError"
        ? "security"
        : "unknown") {
        this.#errors += 1;
        this.#options.onEvent?.(entry.binding.nodeId, Object.freeze({ code, message: cause instanceof Error ? cause.message : String(cause) }));
        this.reportMetrics();
    }
    listen(entry, type, listener) {
        entry.video.addEventListener(type, listener);
        entry.listeners.push([type, listener]);
    }
    disposeEntry(entry) {
        entry.generation += 1;
        entry.pendingCapture = false;
        for (const [type, listener] of entry.listeners)
            entry.video.removeEventListener(type, listener);
        const video = entry.video;
        if (entry.frameCallback !== undefined)
            video.cancelVideoFrameCallback?.(entry.frameCallback);
        if (entry.animationFrame !== undefined)
            cancelAnimationFrame(entry.animationFrame);
        entry.video.pause();
        entry.video.removeAttribute("src");
        entry.video.load();
    }
    isCurrent(entry) {
        return this.#entries.get(entry.binding.nodeId) === entry && !this.#closed;
    }
    requireEntry(nodeId) {
        this.assertOpen();
        const entry = this.#entries.get(nodeId);
        if (entry === undefined)
            throw new Error(`video node ${String(nodeId)} is not mounted`);
        return entry;
    }
    assertOpen() {
        if (this.#closed)
            throw new Error("media pipeline is closed");
    }
    reportMetrics() {
        this.#options.onMetrics?.(this.metrics());
    }
    #handleVisibility = () => {
        if (typeof document === "undefined")
            return;
        for (const entry of this.#entries.values()) {
            if (document.hidden) {
                entry.wasPlayingBeforeHidden = !entry.video.paused;
                if (entry.wasPlayingBeforeHidden)
                    entry.video.pause();
            }
            else if (entry.wasPlayingBeforeHidden) {
                entry.wasPlayingBeforeHidden = false;
                void entry.video.play().catch((cause) => this.emitError(entry, cause));
            }
        }
    };
}
function samePlaybackSource(left, right) {
    return (left.src === right.src &&
        left.crossOrigin === right.crossOrigin &&
        left.preload === right.preload);
}
function closeSource(source) {
    const close = source.close;
    if (typeof close === "function")
        close.call(source);
}
function finiteOrZero(value) {
    return Number.isFinite(value) && value >= 0 ? value : 0;
}
//# sourceMappingURL=media.js.map